import { BellOutlined } from "@ant-design/icons"
import { Badge, Button, Popover, Skeleton } from "antd"
import dayjs from "dayjs"
import React, {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { fetchNotificationCounts } from "../../../redux/notificationCounts/notification-counts.thunks.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import type { RootState } from "../../../store.ts"
import { showDesktopNotification } from "../../../utils/notification/browser-notification.ts"
import { showNotification } from "../../../utils/notification/show-notification"
import { calendarDeepLink } from "../../Calendar/helpers/calendar-deep-link.ts"
import { desktopWantedFor } from "../helpers/desktopPreference.ts"
import { fetchNotificationSettings } from "../redux/notifications.thunks.ts"
import { NOTIFICATION_PATHS } from "../api/services"
import { isNotificationClickable } from "../helpers/isNotificationClickable.ts"
import { markNotificationRead } from "../helpers/markNotificationRead.ts"
import { notificationHref } from "../helpers/notificationHref.ts"
import { keepUnreadMembers } from "../helpers/keepUnreadMembers.ts"
import { parseNotificationMessage } from "../helpers/parseNotificationMessage.ts"
import {
  archiveReadNotifications,
  archiveNotifications,
  markAsRead,
  notificationArrived,
  setConnected,
  setHasNewNotification,
  setCentreOpen,
  setInitialNotifications,
  setUnreadCount
} from "../redux/notification.slice.ts"
import {
  NOTIFICATION_SECTION,
  NOTIFICATION_TYPE,
  type Notification,
  type NotificationCursor,
  WS_MESSAGE_TYPE
} from "../types/notification.types.ts"
import { contentDestination } from "../../../components/notifications/contentDestination.ts"
import { notificationContent } from "../helpers/notificationContent.ts"
import {
  messageToast,
  openNotificationToast,
  reminderToast
} from "../helpers/notificationToast.tsx"
import { DATE_FORMATS_OBJ } from "../../TeamManagementPage/constants/date-formats.ts"
import { NotificationsContent } from "./NotificationsContent.tsx"
import type {
  NotificationCounts,
  NotificationFilter
} from "./NotificationFilterChips.tsx"
import { NotificationsPermissionsAlert } from "./NotificationsPermissionsAlert.tsx"
import { NotificationsPopoverHeader } from "./NotificationsPopoverHeader.tsx"
import { announceAutomaticMove } from "../../StatusAutomation/helpers/automatic-move-events.ts"

const NotificationsModal = lazy(() => import("./NotificationsModal.tsx"))

interface NotificationsController extends ConnectedProps<typeof connector> {
  trigger?: (unreadCount: number) => React.ReactNode
}

function NotificationsController({
  notificationsInitial,
  unreadCount,
  hasNewNotification,
  accessToken,
  setConnected,
  setInitialNotifications,
  setHasNewNotification,
  notificationArrived,
  markAsRead,
  archiveNotifications,
  archiveReadNotifications,
  trigger,
  hasMoreInitial,
  nextInitial,
  setUnreadCount,
  isConnected,
  isMobile,
  hasReceivedInitial,
  isCentreOpenRequest,
  setCentreOpen
}: NotificationsController) {
  const navigate = useNavigate()
  // Presence already stops the server notifying somebody who is looking at the
  // conversation, but it lags a few seconds; this is the client half of that
  // rule, so the message you are reading never raises a toast at you.
  const activeConversation = useAppSelector(
    // Optional on purpose: this controller mounts wherever the bell does,
    // including screens that never load the messaging slice.
    (state) => state.messaging?.activeConversationId ?? null
  )
  const activeConversationRef = useRef<string | null>(null)
  useEffect(() => {
    activeConversationRef.current = activeConversation
  }, [activeConversation])

  // The in-tab desktop alert answers to the same Desktop switch as Web Push,
  // so a type turned off in settings stays off here too.
  const notificationSettings = useAppSelector(
    (state) => state.notification?.notificationSettings ?? null
  )
  const notificationSettingsRef = useRef(notificationSettings)
  useEffect(() => {
    notificationSettingsRef.current = notificationSettings
  }, [notificationSettings])
  const dateFormatId = useAppSelector(
    (state) => state.auth?.userInfo?.date_format
  )
  const dateFormatRef = useRef("MMM D, YYYY")
  useEffect(() => {
    dateFormatRef.current =
      (dateFormatId && DATE_FORMATS_OBJ[dateFormatId]?.format) || "MMM D, YYYY"
  }, [dateFormatId])
  const wsRef = useRef<WebSocket | null>(null)
  const reset = useRef(true)

  // The bell no longer reads this slice. The fetch stays because nothing else
  // populates it and the sidebar's badges do read it.
  const dispatch = useAppDispatch()
  // Four of these six sources are real lists the active branch narrows
  // everywhere else (Proposals/Contracts/Document reviews/Subcontractors)
  // — re-fetching on a branch switch is what keeps a stale "1" from a
  // previously-selected branch from lingering on the badge.
  const activeBranchId = useAppSelector(selectActiveBranchId)

  useEffect(() => {
    dispatch(fetchNotificationCounts())
  }, [dispatch, activeBranchId])

  useEffect(() => {
    if (!notificationSettingsRef.current) {
      void dispatch(fetchNotificationSettings({ silent: true }))
    }
  }, [dispatch])

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [next, setNext] = useState<NotificationCursor>(null)
  const [fetching, setIsFetching] = useState(false)
  const [permissionsNotification, setPermissionsNotification] =
    useState<Notification | null>(null)
  const [counts, setCounts] = useState<NotificationCounts | null>(null)
  const [errored, setErrored] = useState(false)

  // Narrowing is applied in the database, not over the rows the socket
  // happens to have delivered: a search has to reach the whole history, and
  // a count beside a filter chip has to be the count of the list behind it.
  // The socket carries the params (see
  // docs/kb/flows/realtime-notifications.md — reads go over it too).
  const [filter, setFilter] = useState<NotificationFilter>("all")
  const [search, setSearch] = useState("")
  const [categories, setCategories] = useState<string[]>([])
  const [isCustomerActivityOnly, setIsCustomerActivityOnly] = useState(false)

  const narrowing = useMemo(
    () => ({
      search: search || undefined,
      unread_only: filter === "unread" || undefined,
      needs_attention: filter === "needs_attention" || undefined,
      categories: categories.length ? categories : undefined,
      customer_activity: isCustomerActivityOnly || undefined
    }),
    [search, filter, categories, isCustomerActivityOnly]
  )
  const narrowingRef = useRef(narrowing)
  useEffect(() => {
    narrowingRef.current = narrowing
  }, [narrowing])

  const isFiltered =
    Boolean(search) ||
    categories.length > 0 ||
    filter !== "all" ||
    isCustomerActivityOnly

  // The server narrows the page, and these three narrow what is already on
  // screen while it answers — so a chip responds on the click rather than
  // after a round trip. Each is exactly what the server applies, read off
  // the row's own payload, so the two can never disagree: the refined page
  // simply replaces the same rows. It also keeps a build honest against a
  // backend that predates the params and answers unnarrowed.
  const visibleNotifications = useMemo(
    () =>
      notifications.filter((row) => {
        if (isCustomerActivityOnly && !row.is_customer_activity) return false
        if (filter === "unread" && !row.unread) return false
        if (
          filter === "needs_attention" &&
          (row.section ?? NOTIFICATION_SECTION.RECENT_UPDATES) !==
            NOTIFICATION_SECTION.NEEDS_ATTENTION
        ) {
          return false
        }
        return true
      }),
    [notifications, isCustomerActivityOnly, filter]
  )

  useEffect(() => {
    if (reset.current) {
      setNotifications(notificationsInitial)
      setHasMore(hasMoreInitial)
      setNext(nextInitial)
    } else {
      reset.current = true
    }
  }, [
    notificationsInitial,
    hasMoreInitial,
    nextInitial,
    isPopoverOpen,
    isModalOpen
  ])

  useEffect(() => {
    if (!accessToken) return

    // Heartbeat keeps the socket under the ALB's 60s idle timeout.
    const HEARTBEAT_MS = 25000
    const RECONNECT_BASE_MS = 1000
    const RECONNECT_MAX_MS = 30000
    let heartbeat: ReturnType<typeof setInterval> | undefined
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let reconnectAttempts = 0
    let closedByCleanup = false

    const connect = () => {
      const ws = new WebSocket(NOTIFICATION_PATHS.getNotifications(accessToken))
      wsRef.current = ws

      ws.onopen = () => {
        setConnected(true)
        reconnectAttempts = 0
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: WS_MESSAGE_TYPE.PING }))
          }
        }, HEARTBEAT_MS)
      }

      ws.onclose = () => {
        if (heartbeat) clearInterval(heartbeat)
        // Ignore a stale socket's close (a remount already replaced it).
        if (closedByCleanup || ws !== wsRef.current) return
        setConnected(false)
        const backoff = Math.min(
          RECONNECT_BASE_MS * 2 ** reconnectAttempts,
          RECONNECT_MAX_MS
        )
        reconnectAttempts += 1
        reconnectTimer = setTimeout(connect, backoff + Math.random() * 1000)
      }

      ws.onmessage = (event) => {
        const data = parseNotificationMessage(event.data)
        if (!data) return

        if (
          (data.type === WS_MESSAGE_TYPE.INITIAL_STATE ||
            data.type === WS_MESSAGE_TYPE.GET_ALL_MESSAGES) &&
          data.notifications
        ) {
          setErrored(false)
          setInitialNotifications({
            notifications: data.notifications,
            unreadCount: data.unread_count || 0,
            hasMore: data.has_more || false,
            next: data.next_cursor
          })
          setIsFetching(false)
        }

        if (
          data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW &&
          data.notification
        ) {
          notificationArrived(data.notification)
          announceAutomaticMove(data.notification)
        }

        // A reminder is time-sensitive — the badge alone (the default for
        // every other notification type) is too easy to miss, so this one
        // also gets an in-app toast and, permission allowing, a desktop
        // notification. Both reuse the same "Open event" action.
        //
        // Checked against `notification_name`, never `type`: `type` is the
        // broad category ("calendar") shared by every calendar notification
        // — an assignment, a reschedule, a cancellation ALL carry
        // `type: "calendar"` too, and `event_id` rides along on every one of
        // them (see calendars/notifications.py's `_notify`/`_deferred_notifier`
        // helpers). `notification_name` is the specific numeric code (111)
        // that actually distinguishes a reminder from the rest. Confirmed
        // live: comparing against `type` here made this block unreachable
        // against the real backend — it only "passed" in unit tests whose
        // mock fixture set the wrong field's value. `Number(...)` guards the
        // same numeric-vs-string wire-format risk either field could carry.
        //
        // `notification_name` itself is a NEW WS field (not yet deployed as
        // of this comment) — a backend predating it never sends the key at
        // all, so `reminder.verb === "reminder for"` (the literal string
        // `calendars/notifications.py`'s sweep uses for this exact
        // notification, and only this one) is the fallback until that
        // deploys. Drop the fallback once every environment has it.
        if (
          data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW &&
          data.notification?.data?.event_id
        ) {
          const reminder = data.notification

          const isReminder =
            reminder.notification_name != null
              ? Number(reminder.notification_name) ===
                NOTIFICATION_TYPE.CALENDAR_EVENT_REMINDER
              : reminder.verb === "reminder for"

          if (isReminder) {
            const eventId = reminder.data?.event_id
            // In-app SPA navigation, not window.open: EZBuild is already the
            // active tab whenever this toast (or the plain-Notification
            // click below) can fire — both paths only ever run while a tab
            // is open, unlike the closed-browser Web Push/service-worker
            // path, which genuinely may have no existing window to reuse and
            // is left untouched.
            const openEvent = () => {
              navigate(calendarDeepLink(undefined, eventId))
            }

            // `target.name` is the event's own title (see ezbuild_back's
            // `_serialize_generic_object`, which reads `obj.title` for a
            // CalendarEvent) — a clean signal that doesn't depend on parsing
            // the human-readable `description` sentence apart.
            const eventTitle =
              reminder.target?.name || reminder.description || reminder.verb
            const startAt = reminder.data?.start_at
            const endAt = reminder.data?.end_at
            const start = startAt ? dayjs(startAt).tz() : null
            // `end_at` is a NEW WS field, same deploy caveat as
            // notification_name above — absent on an older backend, in which
            // case the range degrades to a single start time rather than
            // showing nothing.
            const end = endAt ? dayjs(endAt).tz() : null
            const dayLabel = start
              ? start.isSame(dayjs().tz(), "day")
                ? "Today"
                : start.format("MMM D")
              : null
            const timeLabel = start
              ? end
                ? `${start.format("h:mm A")} – ${end.format("h:mm A")}`
                : start.format("h:mm A")
              : null
            const whenLabel =
              dayLabel && timeLabel ? `${dayLabel} · ${timeLabel}` : null

            openNotificationToast(
              reminderToast(reminder, {
                t,
                dateFormat: dateFormatRef.current
              }),
              {
                key: `notification-toast-${reminder.id}`,
                duration: 0,
                role: "alert",
                onAction: openEvent
              }
            )

            if (
              desktopWantedFor(
                notificationSettingsRef.current,
                NOTIFICATION_TYPE.CALENDAR_EVENT_REMINDER
              )
            ) {
              showDesktopNotification({
                title: eventTitle,
                body: whenLabel || reminder.description || reminder.verb,
                tag: `notification-${reminder.id}`,
                onClick: openEvent
              })
            }
          }
        }

        // A message is like a reminder in the one way that matters here: the
        // badge alone is too easy to miss while you are working in another
        // part of the app. So a messaging notification also gets an in-app
        // toast and, permission allowing, a desktop alert — both opening the
        // conversation at that message.
        //
        // The server has already decided who should hear about this: it skips
        // the sender, honours All / Mentions only / Muted, and drops anyone
        // presence says is looking at the conversation right now. The guard
        // below is the second half of that last rule — presence can lag a few
        // seconds, and a toast for the message you are reading is noise.
        if (
          data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW &&
          data.notification?.data?.conversation_id
        ) {
          const incoming = data.notification
          const name = Number(incoming.notification_name)
          const isMessaging =
            name === NOTIFICATION_TYPE.MESSAGE_DIRECT ||
            name === NOTIFICATION_TYPE.MESSAGE_CUSTOMER ||
            name === NOTIFICATION_TYPE.MESSAGE_MENTION

          const watchingThisConversation =
            document.visibilityState === "visible" &&
            activeConversationRef.current === incoming.data?.conversation_id

          if (isMessaging && !watchingThisConversation) {
            const sender = incoming.actor?.name ?? ""
            const projectName = incoming.data?.project_name ?? ""
            const preview = incoming.description ?? ""
            const title = sender
              ? `${sender}${projectName ? ` · ${projectName}` : ""}`
              : projectName

            const openConversation = () => {
              const href = notificationHref(incoming)
              if (href) navigate(href)
            }

            const href = notificationHref(incoming)
            openNotificationToast(
              messageToast(incoming, { t, canOpen: Boolean(href) }),
              {
                key: `notification-toast-${incoming.id}`,
                duration: 6,
                role: "status",
                onAction: href ? openConversation : undefined
              }
            )

            if (desktopWantedFor(notificationSettingsRef.current, name)) {
              showDesktopNotification({
                title: title || t("messages.toast.newMessage", "New message"),
                body: preview,
                tag: `notification-${incoming.id}`,
                onClick: openConversation
              })
            }
          }
        }

        if (data.type === WS_MESSAGE_TYPE.NOTIFICATIONS_BATCH) {
          if (data.notifications) {
            setNotifications((prev) => [
              ...prev,
              ...(data.notifications as Notification[])
            ])
          }

          setHasMore(data.has_more || false)
          setNext(data.next_cursor)
        }

        // The server owns this number and every reply that knows it carries
        // it, so the field decides rather than a list of message types.
        if (typeof data.unread_count === "number") {
          setUnreadCount(data.unread_count)
        }

        // Same rule for the chip counts: whichever reply knows them carries
        // them, so marking one row read updates every chip at once.
        if (data.counts) {
          setCounts(data.counts)
        }

        if (data.type === WS_MESSAGE_TYPE.ERROR) {
          setIsFetching(false)
          setErrored(true)
        }

        if (
          data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW &&
          data.notification?.type === NOTIFICATION_TYPE.PERMISSIONS &&
          data?.notification?.unread
        ) {
          setPermissionsNotification(data.notification)
        }
      }
    }

    connect()

    return () => {
      closedByCleanup = true
      if (heartbeat) clearInterval(heartbeat)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      wsRef.current?.close()
      // Reset here too: onclose returns early when closedByCleanup is set.
      setConnected(false)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the socket is opened once per session; t only words a toast when a message arrives and must not reconnect it
  }, [
    accessToken,
    navigate,
    setConnected,
    setInitialNotifications,
    setHasNewNotification,
    notificationArrived,
    setUnreadCount
  ])

  const { t } = useTranslation()

  const sendWhenOpen = (payload: Record<string, unknown>): boolean => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      showNotification.warning({
        message: t("notifications.bell.actionUnavailable")
      })
      return false
    }

    wsRef.current.send(JSON.stringify(payload))
    return true
  }

  // A row stands for every update behind it, and the server takes one id at a
  // time, so what is sent has to reach exactly what the local update changes.
  const idsFor = (target: number | number[]): number[] => {
    const ids = new Set(Array.isArray(target) ? target : [target])
    for (const row of notifications) {
      if (!ids.has(row?.id)) continue
      for (const member of row?.group_members ?? []) ids.add(member?.id)
    }
    return [...ids]
  }

  const handleMarkAsRead = (target: number | number[] | null) => {
    reset.current = false

    if (target === null) {
      if (
        !sendWhenOpen({
          type: WS_MESSAGE_TYPE.MARK_READ,
          notification_id: null
        })
      ) {
        reset.current = true
        return
      }

      markAsRead(null)
      setNotifications((prev) => prev.map(markNotificationRead))
      return
    }

    const ids = idsFor(target)
    const sent = ids.every((id) =>
      sendWhenOpen({ type: WS_MESSAGE_TYPE.MARK_READ, notification_id: id })
    )
    if (!sent) {
      reset.current = true
      return
    }

    const read = new Set(ids)
    markAsRead(ids)
    setNotifications((prev) =>
      prev.map((n) => (read.has(n.id) ? markNotificationRead(n) : n))
    )
  }

  const onLoadMore = () => {
    sendWhenOpen({
      type: WS_MESSAGE_TYPE.FETCH_MORE,
      after_id: next,
      ...narrowingRef.current
    })
  }

  const handleFetchNotifications = () => {
    setIsFetching(true)
    if (
      !sendWhenOpen({
        type: WS_MESSAGE_TYPE.GET_ALL_MESSAGES,
        ...narrowingRef.current
      })
    ) {
      setIsFetching(false)
      return
    }
    setHasNewNotification(false)
  }

  // A changed filter is a new query, not a client-side slice of the loaded
  // rows: the page is re-asked for from the server so a search reaches the
  // whole history. Skipped until the first page has arrived, or the initial
  // connect would be re-requested the moment it answered.
  const narrowingKey = JSON.stringify(narrowing)
  const lastRequestedNarrowing = useRef(narrowingKey)
  useEffect(() => {
    if (!hasReceivedInitial) return
    if (lastRequestedNarrowing.current === narrowingKey) return

    const previous = JSON.parse(
      lastRequestedNarrowing.current
    ) as typeof narrowing
    lastRequestedNarrowing.current = narrowingKey

    // Only a search or a category change blanks the list while the server
    // answers: those two cannot be approximated from the rows already here,
    // so leaving the old ones under a new term would be a lie. The chips
    // and the customer-activity toggle narrow what is loaded straight away
    // (see `visibleNotifications`) and the server's page refines it, so a
    // skeleton there is a flash for nothing.
    const needsServerAnswer =
      previous.search !== narrowing.search ||
      JSON.stringify(previous.categories) !==
        JSON.stringify(narrowing.categories)
    if (needsServerAnswer) setIsFetching(true)

    if (
      !sendWhenOpen({
        type: WS_MESSAGE_TYPE.GET_ALL_MESSAGES,
        ...narrowing
      })
    ) {
      setIsFetching(false)
    }
    // `narrowingKey` stands for `narrowing` here: the object is rebuilt each
    // render, its serialization is not, and re-running on the object would
    // re-request the page every time.
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- narrowingKey is narrowing serialised; the object is rebuilt on every render and would re-request the page each time
  }, [narrowingKey, hasReceivedInitial])

  const handleArchive = (target: number | number[] | null) => {
    reset.current = false

    // A null target means "everything read", not "everything".
    if (target === null) {
      if (!sendWhenOpen({ type: WS_MESSAGE_TYPE.ARCHIVE_ALL_READ })) {
        reset.current = true
        return
      }

      archiveReadNotifications()
      setNotifications((prev) =>
        prev.map(keepUnreadMembers).filter((n): n is Notification => n !== null)
      )
      return
    }

    const ids = idsFor(target)
    const sent = ids.every((id) =>
      sendWhenOpen({ type: WS_MESSAGE_TYPE.ARCHIVE, id })
    )
    if (!sent) {
      reset.current = true
      return
    }

    const archived = new Set(ids)
    archiveNotifications(ids)
    setNotifications((prev) => prev.filter((n) => !archived.has(n.id)))
  }

  const handleCardClick = (notification: Notification) => {
    // The content block names the record and the exact tab to open; a
    // notification from a backend that predates it falls back to what the
    // old payload could resolve.
    const content = notificationContent(notification)
    const href =
      contentDestination(content?.action?.target) ??
      (isNotificationClickable(notification)
        ? notificationHref(notification)
        : null)
    if (!href) return

    // In-app navigation, not a new tab: a review action opens a record in the
    // app the reader is already in, and a new tab loses the bell they came
    // from. Reading is not resolving, so the row's own state is untouched.
    navigate(href)
    setIsPopoverOpen(false)
    setIsModalOpen(false)
  }

  const openModal = () => {
    setIsPopoverOpen(false)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    if (isCentreOpenRequest) setCentreOpen(false)
  }

  // Home's notifications widget has no bell of its own, so its "View all"
  // asks for the centre through the slice rather than owning a second one.
  useEffect(() => {
    if (isCentreOpenRequest) {
      setIsPopoverOpen(false)
      setIsModalOpen(true)
    }
  }, [isCentreOpenRequest])

  const bell = trigger ? (
    trigger(unreadCount)
  ) : (
    <Badge
      count={unreadCount}
      offset={unreadCount > 9 ? [-8, 2] : [-4, 2]}
      className="z-10"
    >
      <Button
        type="text"
        icon={<BellOutlined />}
        className="shadow-sm border border-gray-100"
        size="large"
      />
    </Badge>
  )

  return (
    <>
      {permissionsNotification && (
        <Suspense>
          <NotificationsPermissionsAlert
            permissionsNotification={permissionsNotification}
            onMarkAsRead={handleMarkAsRead}
            onPermissionsNotification={() => setPermissionsNotification(null)}
          />
        </Suspense>
      )}
      {isMobile ? (
        // The popover is right-aligned to a mid-header trigger, so it cannot fit a phone.
        <span className="inline-flex" onClick={() => setIsModalOpen(true)}>
          {bell}
        </span>
      ) : (
        <Popover
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(value) => setIsPopoverOpen(value)}
          placement="bottomRight"
          styles={{ body: { padding: 0 } }}
          title={
            <NotificationsPopoverHeader
              unreadCount={unreadCount}
              counts={counts}
              onMarkAllAsRead={() => handleMarkAsRead(null)}
              disabled={!isConnected}
              filter={filter}
              onFilterChange={setFilter}
              isCustomerActivityOnly={isCustomerActivityOnly}
              onToggleCustomerActivityOnly={() =>
                setIsCustomerActivityOnly((value) => !value)
              }
            />
          }
          content={
            <NotificationsContent
              notifications={visibleNotifications}
              hasNewNotification={hasNewNotification}
              onMarkAsRead={handleMarkAsRead}
              onCardClick={handleCardClick}
              onArchive={handleArchive}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              refetchNotifications={handleFetchNotifications}
              fetching={fetching || !hasReceivedInitial}
              isFiltered={isFiltered}
              errored={errored}
              onViewAll={openModal}
              density="compact"
            />
          }
        >
          {bell}
        </Popover>
      )}

      {isModalOpen && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className={"fixed inset-0 z-10  m-auto h-[90vh] w-[90vw]"}
            />
          }
        >
          <NotificationsModal
            open={isModalOpen}
            onClose={closeModal}
            notifications={visibleNotifications}
            unreadCount={unreadCount}
            counts={counts}
            onMarkAsRead={handleMarkAsRead}
            onCardClick={handleCardClick}
            onArchive={handleArchive}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            refetchNotifications={handleFetchNotifications}
            hasNewNotification={hasNewNotification}
            fetching={fetching || !hasReceivedInitial}
            disabled={!isConnected}
            isMobile={isMobile}
            filter={filter}
            onFilterChange={setFilter}
            search={search}
            onSearchChange={setSearch}
            categories={categories}
            onCategoriesChange={setCategories}
            isCustomerActivityOnly={isCustomerActivityOnly}
            onToggleCustomerActivityOnly={() =>
              setIsCustomerActivityOnly((value) => !value)
            }
            errored={errored}
          />
        </Suspense>
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  accessToken: state.auth.accessToken,
  notificationsInitial: state.notification.notifications,
  unreadCount: state.notification.unreadCount,
  hasNewNotification: state.notification.hasNewNotification,
  hasMoreInitial: state.notification.hasMore,
  nextInitial: state.notification.next,
  isConnected: state.notification.isConnected,
  isMobile: state.nav.isMobile,
  hasReceivedInitial: state.notification.hasReceivedInitial,
  isCentreOpenRequest: state.notification.isCentreOpen
})

const mapDispatchToProps = {
  markAsRead,
  setConnected,
  setHasNewNotification,
  notificationArrived,
  setInitialNotifications,
  archiveNotifications,
  archiveReadNotifications,
  setUnreadCount,
  setCentreOpen
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(NotificationsController)
